/**
 * Retouche du SDP audio sortant (offre/réponse locale) pour une voix la plus
 * robuste possible sur la liaison Internet agent ↔ serveur Asterisk.
 *
 * POURQUOI : la jambe agent passe en WebRTC à travers le NAT/Internet (jitter,
 * pertes de paquets). G.711/G.722 n'ont AUCUNE correction de perte → la moindre
 * perte = clic / mot haché / voix « robotisée ». Opus embarque un FEC in-band
 * (`useinbandfec=1`) + une dissimulation de perte (PLC) qui reconstruit les
 * paquets manquants → voix continue et claire même sur réseau imparfait.
 *
 * Ces fonctions sont PURES (string → string) : aucune dépendance navigateur,
 * donc testables unitairement. Le branchement WebRTC se fait dans le
 * SipController via l'évènement `sdp` de JsSIP (uniquement sur le SDP LOCAL).
 *
 * Comportement de repli : si l'autre extrémité (Asterisk) n'a PAS proposé Opus
 * dans la session, le SDP ne contient pas de `rtpmap … opus` → on renvoie le
 * SDP inchangé. Aucune régression possible quand Opus n'est pas disponible.
 */

/** `useinbandfec=1` : FEC in-band (anti-perte). `usedtx=0` : pas de coupure en
 *  silence (évite les micro-troncatures à la reprise de parole, important en
 *  centre d'appels). `stereo=0` : mono (la voix n'a aucun besoin de stéréo et
 *  ça économise la bande passante). */
const OPUS_FMTP: Record<string, string> = {
  useinbandfec: '1',
  usedtx: '0',
  stereo: '0',
}

/** Parse une chaîne fmtp `a=fmtp` (« k=v;k=v ») en objet, en préservant l'ordre. */
function parseFmtp(params: string): Map<string, string> {
  const out = new Map<string, string>()
  for (const kv of params.split(';')) {
    const t = kv.trim()
    if (!t) continue
    const eq = t.indexOf('=')
    if (eq === -1) out.set(t, '')
    else out.set(t.slice(0, eq).trim(), t.slice(eq + 1).trim())
  }
  return out
}

/** Sérialise une map fmtp en « k=v;k=v ». */
function formatFmtp(m: Map<string, string>): string {
  const parts: string[] = []
  for (const [k, v] of m) parts.push(v === '' ? k : `${k}=${v}`)
  return parts.join(';')
}

/** Fusionne les paramètres Opus souhaités dans une chaîne fmtp existante
 *  (override des clés gérées, conservation des autres). */
function mergeOpusFmtp(existing: string): string {
  const m = parseFmtp(existing)
  for (const [k, v] of Object.entries(OPUS_FMTP)) m.set(k, v)
  return formatFmtp(m)
}

/**
 * Réordonne la m-line audio pour préférer Opus et ajoute/complète son `fmtp`
 * avec le FEC in-band. No-op si Opus n'est pas présent dans la section audio.
 */
export function preferOpusAudio(sdp: string): string {
  if (!sdp || sdp.indexOf('m=audio') === -1) return sdp
  const eol = sdp.includes('\r\n') ? '\r\n' : '\n'
  const lines = sdp.split(/\r\n|\r|\n/)

  // Délimite la section média audio : [mIdx, end).
  let mIdx = -1
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith('m=audio')) { mIdx = i; break }
  }
  if (mIdx === -1) return sdp
  let end = lines.length
  for (let i = mIdx + 1; i < lines.length; i++) {
    if (lines[i].startsWith('m=')) { end = i; break }
  }

  // Repère le payload type d'Opus (rtpmap … opus/48000[/2]).
  const rtpmapRe = /^a=rtpmap:(\d+)\s+opus\/48000(?:\/2)?\s*$/i
  let opusPt = ''
  let opusRtpmapIdx = -1
  for (let i = mIdx + 1; i < end; i++) {
    const mm = lines[i].match(rtpmapRe)
    if (mm) { opusPt = mm[1]; opusRtpmapIdx = i; break }
  }
  if (!opusPt) return sdp  // Opus absent → on ne touche à rien.

  // 1) Réordonne les payloads de la m-line : Opus en tête. Split tolérant aux
  //    espaces multiples (`/\s+/`) pour ne pas créer de jetons vides.
  const parts = lines[mIdx].split(/\s+/)
  const head = parts.slice(0, 3)          // m=audio <port> <proto>
  const pts = parts.slice(3).filter((p) => p.length)
  if (pts.includes(opusPt)) {
    lines[mIdx] = [...head, opusPt, ...pts.filter((p) => p !== opusPt)].join(' ')
  }

  // 2) Garantit le fmtp d'Opus avec le FEC.
  const fmtpRe = new RegExp('^a=fmtp:' + opusPt + '\\s+(.*)$')
  let fmtpIdx = -1
  for (let i = mIdx + 1; i < end; i++) {
    if (fmtpRe.test(lines[i])) { fmtpIdx = i; break }
  }
  if (fmtpIdx !== -1) {
    const cur = lines[fmtpIdx].match(fmtpRe)![1]
    lines[fmtpIdx] = `a=fmtp:${opusPt} ${mergeOpusFmtp(cur)}`
  } else {
    const ins = `a=fmtp:${opusPt} ${formatFmtp(new Map(Object.entries(OPUS_FMTP)))}`
    lines.splice(opusRtpmapIdx + 1, 0, ins)
  }

  return lines.join(eol)
}
